/**
 * 「构建于 …」版本标记 —— **全站唯一的实现**（家长端 / 学生端 / 系统管理后台共用）。
 *
 * 为什么要有它：用户发来的截图经常来自**浏览器缓存里的旧构建**，看起来就像新 bug。
 * 界面上摆一行谁都能看到的小灰字之后，"当前跑的是哪个构建"一眼可辨。
 *
 * 为什么把它抽成纯函数（而不是留在某个外壳组件里自己拼）：
 *  1. **同一事实只有一个来源** —— 家长 / 学生端的 `AppShell`、系统管理后台的
 *     `AdminShell`、以及管理端两个独立全屏页（登录、绑定动态口令）都要显示这一行。
 *     各处自己拼字符串，格式迟早会漂移（比如有人把年份去掉、或换个分隔符）。
 *  2. 拼装逻辑是纯函数，可以在 `vitest` 里直接测 —— 本目录的测试**只覆盖纯函数**
 *     （见 `apps/web/vitest.config.ts`），渲染层仍由"真机构建 + 检查脚本"覆盖。
 */
import { formatDateTimeFull } from './utils';

/**
 * 构建时刻 → `'构建于 YYYY-MM-DD HH:mm'`（家庭时区，zh-CN）。
 *
 * ⚠️ 格式**不在这里发明**：时间部分一律走 `formatDateTimeFull`，与账号台账、
 * 审计日志、备份时间等界面**同一套**（`'YYYY-MM-DD HH:mm'`，带年份）。
 *
 * 兜底：`null` / `undefined` / 空串时 `formatDateTimeFull` 返回 `'—'`，
 * 于是这里是 `'构建于 —'` —— **不会**渲染出 `undefined`，也不会留一片空白。
 */
export function formatBuildStamp(buildTime: string | null | undefined): string {
  return `构建于 ${formatDateTimeFull(buildTime)}`;
}

/**
 * 读取构建时常量 `__BUILD_TIME__`（由 `apps/web/vite.config.ts` 的 `define`
 * 在打包/开发时注入，类型声明见 `src/vite-env.d.ts`）。
 *
 * 为什么要包这一层（而不是直接写 `__BUILD_TIME__`）：
 *   `typeof` 一个**没有被 define 注入**的全局标识符不会抛错，直接引用却会抛
 *   `ReferenceError`。万一哪天 define 被误删或写错名字，这里退化成 `null`
 *   （界面显示 `'构建于 —'`），总好过整个管理后台/家长端当场白屏。
 *
 * 值只有**一份**：家长端与管理端引用的是同一个常量、同一个函数，所以两边要么
 * 同时有值（同一次构建注入的同一个时间），要么同时显示 `'—'`，不会一边有一边空。
 */
export function readBuildTime(): string | null {
  return typeof __BUILD_TIME__ === 'string' && __BUILD_TIME__.length > 0 ? __BUILD_TIME__ : null;
}
